import { useEffect } from 'react'
import { getApiErrorStatus } from '@shared/api/client'

/** 404 «Invalid page» на странице дальше первой: список сократился (фильтр,
 *  удаление последней строки), и сохранённой страницы больше нет. */
export function isInvalidPage(error: unknown, page: number): boolean {
  return page > 1 && getApiErrorStatus(error) === 404
}

export interface PagedQueryState {
  /** Запрошенной страницы больше нет — её надо сменить на первую, а не показывать ошибку. */
  invalidPage: boolean
  isLoading: boolean
  isError: boolean
}

/** Состояние страничного запроса для экрана: «нет страницы» — не ошибка, а
 *  загрузка первой (пока `setPage(1)` не отработает). */
export function resolvePagedState(
  query: { isLoading: boolean; isError: boolean; error: unknown },
  page: number
): PagedQueryState {
  const invalidPage = query.isError && isInvalidPage(query.error, page)
  return {
    invalidPage,
    isLoading: query.isLoading || invalidPage,
    isError: query.isError && !invalidPage,
  }
}

interface PagedQuery {
  data: { count: number } | undefined
  isLoading: boolean
  isError: boolean
  error: unknown
}

interface PagedControls {
  page: number
  setPage: (page: number) => void
  setTotalItems: (total: number) => void
}

/**
 * Общая обвязка страничного списка: счётчик страниц берётся из ответа, а
 * страница, которой больше нет (список сократился), откатывается на первую
 * вместо ошибки.
 */
export function usePagedQueryState(
  query: PagedQuery,
  { page, setPage, setTotalItems }: PagedControls
): PagedQueryState {
  useEffect(() => {
    if (query.data?.count != null) setTotalItems(query.data.count)
  }, [query.data?.count, setTotalItems])

  const state = resolvePagedState(query, page)
  const { invalidPage } = state
  useEffect(() => {
    if (invalidPage) setPage(1)
  }, [invalidPage, setPage])

  return state
}

/** Для любого запроса, ошибку которого показывает сам экран (пусто, повтор,
 *  ошибка фильтра): общий тост погашен, иначе откат с несуществующей страницы
 *  на первую успевает выбросить красный тост. */
export const QUIET_QUERY = { meta: { silent: true } } as const
